import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from './components/Navbar';
import HeroBanner from './components/HeroBanner';
import ExploreSection from './components/ExploreSection';
import LevelsSection from './components/LevelsSection';
import ReviewsSection from './components/ReviewsSection';
import FeaturedTrips from './components/FeaturedTrips';
import UpcomingEvents from './components/UpcomingEvents';
import GroupInquiryPromo from './components/GroupInquiryPromo';
import SaleBanner from './components/SaleBanner';
import MagazineSection from './components/MagazineSection';
import WhyChooseUs from './components/WhyChooseUs';
import Footer from './components/Footer';
import TourDetail from './components/TourDetail';
import ToursPage from './components/ToursPage';
import GuideSection from './components/GuideSection';
import BlogCta from './components/BlogCta';
import InstagramFeed from './components/InstagramFeed';
import BlogDetail from './components/BlogDetail';
import BlogListPage from './components/BlogListPage';
import CalendarPage from './components/CalendarPage';
import GroupInquiryPage from './components/GroupInquiryPage';
import WishlistPage from './components/WishlistPage';
import EventPromo from './components/EventPromo';
import LikyaEventPage from './components/events/EventPage';
import WhatsAppButton from './components/WhatsAppButton';
import GoogleReviews from './components/GoogleReviews';
import RouteAnalytics from './components/RouteAnalytics';
import SEO from './components/SEO';
import AboutPage from './components/AboutPage';
import {
  ContactPage, FaqPage, SalesAgreementPage, CancellationPage, KvkkPage,
} from './components/CorporatePages';
import { AuthProvider, useAuth } from './context/AuthContext';
import AdminLogin from './components/admin/AdminLogin';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminCategories from './components/admin/AdminCategories';
import AdminTours from './components/admin/AdminTours';
import AdminTourForm from './components/admin/AdminTourForm';
import AdminReservations from './components/admin/AdminReservations';
import AdminMessages from './components/admin/AdminMessages';
import AdminSettings from './components/admin/AdminSettings';
import AdminBlog from './components/admin/AdminBlog';
import AdminComments from './components/admin/AdminComments';
import AdminEvents from './components/admin/AdminEvents';
import AdminEventForm from './components/admin/AdminEventForm';

const Home = () => (
  <div className="min-h-screen bg-white">
    <SEO
      title="Türkiye'nin En İyi Rehberli Macera Turları"
      description="Camption ile Likya Yolu, Karia Yolu ve Türkiye'nin eşsiz doğa rotalarında TÜRSAB lisanslı rehberler eşliğinde 3-4 gecelik küçük gruplu trekking turları. Konaklama ve ulaşım dahil."
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'TravelAgency',
        name: 'Camption',
        url: typeof window !== 'undefined' ? window.location.origin : '',
        logo: typeof window !== 'undefined' ? `${window.location.origin}/logo.png` : '',
        description: "Türkiye'de doğa ve macera odaklı rehberli yürüyüş turları.",
        areaServed: { '@type': 'Country', name: 'Türkiye' },
        sameAs: ['https://instagram.com/camption'],
      }}
    />
    <Navbar />
    <HeroBanner />
    <UpcomingEvents />
    <EventPromo />
    <WhyChooseUs />
    <FeaturedTrips />
    {/* ExploreSection geçici olarak gizlendi - canlıya almadan tek satır açılır */}
    {/* <ExploreSection /> */}
    <GoogleReviews />
    <GuideSection />
    <BlogCta />
    <GroupInquiryPromo />
    <SaleBanner />
    <MagazineSection />
    <InstagramFeed />
    <Footer />
  </div>
);

const TourPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />
    <TourDetail />
    <Footer />
  </div>
);

const ToursListPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />
    <ToursPage />
    <Footer />
  </div>
);

// Wrap a corporate content page with Navbar + Footer
const Corporate = ({ children }) => (
  <div className="min-h-screen bg-white flex flex-col">
    <Navbar />
    <div className="flex-1">{children}</div>
    <Footer />
  </div>
);

/** Eski /etkinlik/<slug> adreslerini kısa adrese taşır. */
const EventSlugRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/${slug}`} replace />;
};


const AdminRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <AdminLogin />;
  return <AdminLayout>{children}</AdminLayout>;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <RouteAnalytics />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/turlar" element={<ToursListPage />} />
              <Route path="/tur/:slug" element={<TourPage />} />
              <Route path="/hakkimizda" element={<Corporate><AboutPage /></Corporate>} />
              <Route path="/bize-ulasin" element={<Corporate><ContactPage /></Corporate>} />
              <Route path="/yardim-ve-sss" element={<Corporate><FaqPage /></Corporate>} />
              <Route path="/mesafeli-satis-sozlesmesi" element={<Corporate><SalesAgreementPage /></Corporate>} />
              <Route path="/iptal-politikasi" element={<Corporate><CancellationPage /></Corporate>} />
              <Route path="/kvkk" element={<Corporate><KvkkPage /></Corporate>} />
              <Route path="/blog" element={<Corporate><BlogListPage /></Corporate>} />
              <Route path="/takvim" element={<Corporate><CalendarPage /></Corporate>} />
              <Route path="/grup-rezervasyonu" element={<Corporate><GroupInquiryPage /></Corporate>} />
              <Route path="/favoriler" element={<Corporate><WishlistPage /></Corporate>} />
              <Route path="/etkinlik/:slug" element={<EventSlugRedirect />} />
              <Route path="/blog/:slug" element={<Corporate><BlogDetail /></Corporate>} />
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/kategoriler" element={<AdminRoute><AdminCategories /></AdminRoute>} />
              <Route path="/admin/turlar" element={<AdminRoute><AdminTours /></AdminRoute>} />
              <Route path="/admin/turlar/yeni" element={<AdminRoute><AdminTourForm /></AdminRoute>} />
              <Route path="/admin/turlar/:tourId/duzenle" element={<AdminRoute><AdminTourForm /></AdminRoute>} />
              <Route path="/admin/etkinlikler" element={<AdminRoute><AdminEvents /></AdminRoute>} />
              <Route path="/admin/etkinlikler/yeni" element={<AdminRoute><AdminEventForm /></AdminRoute>} />
              <Route path="/admin/etkinlikler/:eventId/duzenle" element={<AdminRoute><AdminEventForm /></AdminRoute>} />
              <Route path="/admin/rezervasyonlar" element={<AdminRoute><AdminReservations /></AdminRoute>} />
              <Route path="/admin/mesajlar" element={<AdminRoute><AdminMessages /></AdminRoute>} />
              <Route path="/admin/blog" element={<AdminRoute><AdminBlog /></AdminRoute>} />
              <Route path="/admin/yorumlar" element={<AdminRoute><AdminComments /></AdminRoute>} />
              <Route path="/admin/ayarlar" element={<AdminRoute><AdminSettings /></AdminRoute>} />
              <Route path="/:slug" element={<Corporate><LikyaEventPage /></Corporate>} />
            </Routes>
            <WhatsAppButton />
          </BrowserRouter>
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
